import { RundownPermission } from '@rundown-studio/types'

import { useAtomValue } from 'jotai'
import { lazy, useCallback, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useTeamsByUserId } from '@/entities/team/index.js'
import { useUser } from '@/entities/user/index.ts'
import { eventAtom } from '@/store/event.store.js'

import { rundownAtom } from '@/store/rundown.store.ts'

import eventBus from '../../../utils/eventBus.js'
import { getValidTeamIdWithFallback } from '../../../utils/index.js'
import { useModal } from '../../modal-manager/index.ts'
import { RundownPermissionGuard } from '../../permissions/ui/rundown-permission-guard.tsx'

import { RundownNavbarName } from './RundownNavbarName.tsx'
import { RundownNavbarStatusMenu } from './RundownNavbarStatusMenu.tsx'
import { RundownNavbarConnectionStatus } from './RundownNavbarConnectionStatus.tsx'
import { RundownNavbarMenu } from './RundownNavbarMenu.tsx'

const UpgradeModal = lazy(() => import('@/components/modal/UpgradeModal.jsx'))
const RundownOutputModal = lazy(
  () => import('@/components/modal/RundownOutputModal.jsx'),
)
const RundownTimezoneModal = lazy(
  () => import('@/components/modal/RundownTimezoneModal.jsx'),
)
const RundownExportModal = lazy(
  () => import('@/components/modal/RundownExportModal.jsx'),
)
const RundownSettingsModal = lazy(
  () => import('@/components/modal/RundownSettingsModal.tsx'),
)
const RundownCollaborateModal = lazy(
  () => import('@/components/modal/RundownCollaborateModal.jsx'),
)
const RundownTrashModal = lazy(
  () => import('@/components/modal/rundown-trash-modal.tsx'),
)

export function RundownNavbar() {
  const rundown = useAtomValue(rundownAtom)
  const event = useAtomValue(eventAtom)

  const [upgradeModal, renderUpgradeModal] = useModal(UpgradeModal)
  const [outputModal, renderOutputModal] = useModal(RundownOutputModal)
  const [timezoneModal, renderTimezoneModal] = useModal(RundownTimezoneModal)
  const [exportModal, renderExportModal] = useModal(RundownExportModal)
  const [settingsModal, renderSettingsModal] = useModal(RundownSettingsModal)
  const [collaborateModal, renderCollaborateModal] = useModal(
    RundownCollaborateModal,
  )
  const [trashModal, renderTrashModal] = useModal(RundownTrashModal)

  const { data: user } = useUser()
  const { data: userTeams } = useTeamsByUserId({
    params: {
      userId: user?.uid,
    },
    options: {
      enabled: !!user,
    },
  })

  // Open timezone modal through button click or global event
  const handleOpenTimezone = useCallback(() => {
    timezoneModal.show()
  }, [timezoneModal])

  // Open trash modal through button click or global event
  const handleOpenTrashModal = useCallback(() => {
    trashModal.show()
  }, [trashModal])

  const [searchParams] = useSearchParams()

  const backUrl = searchParams.get('back')

  const internalBackUrl = useMemo(() => {
    if (backUrl) {
      try {
        const url = new URL(backUrl)
        return url.pathname + url.search
      } catch {
        return backUrl
      }
    }
    if (rundown.eventId && rundown.teamId)
      return `/dashboard/${rundown.teamId}/event/${rundown.eventId}`
    if (rundown.teamId)
      return `/dashboard/${getValidTeamIdWithFallback(rundown.teamId, userTeams ?? [])}`
    return '/dashboard'
  }, [rundown.eventId, rundown.teamId, backUrl, userTeams])

  useEffect(() => {
    eventBus.$on('openTimezoneModal', handleOpenTimezone)
    return () => eventBus.$off('openTimezoneModal', handleOpenTimezone)
  }, [handleOpenTimezone])

  useEffect(() => {
    eventBus.$on('openTrashModal', handleOpenTrashModal)
    return () => eventBus.$off('openTrashModal', handleOpenTrashModal)
  }, [handleOpenTrashModal])

  return (
    <>
      <nav className="h-full bg-black flex items-center gap-2">
        <Link to={internalBackUrl}>
          <img
            src={rundown.logo || event?.logo || '/img/logo.svg'}
            alt="logo"
            className="p-1 h-10 object-contain"
          />
        </Link>

        <span className="grow"></span>

        {event && (
          <>
            <Link
              to={internalBackUrl}
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              {event.name || 'Event'}
            </Link>
            <p className="text-gray-500 text-sm">/</p>
          </>
        )}

        <RundownNavbarName />

        <div className="relative">
          <RundownNavbarStatusMenu />
        </div>

        <span className="grow"></span>

        <div className="px-2">
          <RundownNavbarConnectionStatus />
        </div>

        <div>
          <RundownNavbarMenu
            teamId={rundown?.teamId}
            eventId={rundown?.eventId}
            eventUrl={internalBackUrl}
            onOpenSettings={settingsModal.show}
            onInviteGuests={collaborateModal.show}
            onGenerateOutput={outputModal.show}
            onExportPdf={exportModal.show}
            openUpgradeModal={upgradeModal.show}
            onShowTrash={trashModal.show}
          />
        </div>
      </nav>

      <RundownPermissionGuard permission={RundownPermission.EDIT}>
        {renderTimezoneModal({
          rundownId: rundown.id,
          timezone: rundown.timezone,
        })}
        {renderTrashModal({
          rundownId: rundown.id,
        })}
        {renderExportModal({
          rundownId: rundown.id,
        })}
        {renderOutputModal({ rundownId: rundown.id })}
        {renderCollaborateModal({ rundownId: rundown.id })}
        {renderSettingsModal()}

        {renderUpgradeModal({
          message: 'Upgrade your account to export the rundown.',
        })}
      </RundownPermissionGuard>
    </>
  )
}
